import React, { useState, useEffect } from 'react';
import { logout, getUser, apiCall } from '../lib/api.js';

const QA_ITEMS = [
  { id: 'new-eval', label: 'New evaluation', icon: 'ti-file-plus' },
  { id: 'evaluations', label: 'Evaluations', icon: 'ti-list' },
  { id: 'appeals', label: 'Appeals', icon: 'ti-message-circle' },
  { id: 'dashboard', label: 'Dashboard', icon: 'ti-chart-bar' },
];

const AGENT_ITEMS = [
  { id: 'my-dashboard', label: 'My performance', icon: 'ti-chart-bar' },
  { id: 'my-scorecards', label: 'My scorecards', icon: 'ti-clipboard-check' },
  { id: 'my-appeals', label: 'My appeals', icon: 'ti-message-circle' },
];

const ADMIN_ITEMS = [
  { id: 'agents', label: 'Agents', icon: 'ti-users' },
  { id: 'settings', label: 'Settings', icon: 'ti-settings' },
];

function Tooltip({ label }) {
  return (
    <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
      {label}
    </span>
  );
}

function NavButton({ item, active, collapsed, badge, onClick }) {
  return (
    <button onClick={onClick}
      className={'relative w-full flex items-center gap-2.5 py-2.5 text-sm border-l-[3px] transition-all group '
        + (collapsed ? 'justify-center px-0 ' : 'px-4 ')
        + (active
          ? 'border-purple-600 bg-purple-50 text-gray-900 font-medium'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50')}>
      <i className={'ti ' + item.icon + ' text-base' + (active ? ' text-purple-600' : '')} aria-hidden="true"></i>
      {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
      {!collapsed && badge > 0 && (
        <span className={'px-1.5 py-0.5 rounded-full text-[11px] font-medium '
          + (active ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600')}>{badge}</span>
      )}
      {collapsed && badge > 0 && (
        <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-purple-600 text-white text-[10px] font-medium">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
      {collapsed && <Tooltip label={item.label + (badge > 0 ? ' (' + badge + ')' : '')} />}
    </button>
  );
}

function SidebarContent({ collapsed, currentPage, onNavigate, onToggleCollapse, pendingAppeals, appealsItemId }) {
  const user = getUser();
  const initials = (user?.name || user?.email || '??').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const navItems = isAgent ? AGENT_ITEMS : QA_ITEMS;

  return (
    <>
      {/* Header */}
      <div className={'border-b border-gray-100 flex items-center '
        + (collapsed ? 'flex-col gap-2 py-4' : 'justify-between px-4 py-4')}>
        {collapsed ? (
          <i className="ti ti-clipboard-check text-purple-600 text-lg" aria-hidden="true"></i>
        ) : (
          <div className="min-w-0">
            <h1 className="text-base font-medium flex items-center gap-2">
              <i className="ti ti-clipboard-check text-purple-600 text-lg" aria-hidden="true"></i>
              QA scorecard
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">Sabrent support</p>
          </div>
        )}
        {onToggleCollapse && (
          <button onClick={onToggleCollapse}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <i className={'ti text-base ' + (collapsed ? 'ti-chevron-right' : 'ti-chevron-left')} aria-hidden="true"></i>
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {navItems.map(item => (
          <NavButton key={item.id} item={item} collapsed={collapsed}
            active={currentPage === item.id}
            badge={item.id === appealsItemId ? pendingAppeals : 0}
            onClick={() => onNavigate(item.id)} />
        ))}
        {isAdmin && (
          <>
            {!collapsed && <div className="px-4 pt-5 pb-1.5 text-[11px] text-gray-400 uppercase tracking-wider">Admin</div>}
            {collapsed && <div className="mx-3 my-3 border-t border-gray-100"></div>}
            {ADMIN_ITEMS.map(item => (
              <NavButton key={item.id} item={item} collapsed={collapsed}
                active={currentPage === item.id}
                badge={0}
                onClick={() => onNavigate(item.id)} />
            ))}
          </>
        )}
      </nav>

      {/* Profile */}
      <div className={'border-t border-gray-100 ' + (collapsed ? 'py-3 flex justify-center' : 'px-4 py-3')}>
        {collapsed ? (
          <div className="relative group">
            <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-medium">{initials}</div>
            <Tooltip label={(user?.name || user?.email || '') + ' (' + (user?.role || '') + ')'} />
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-medium">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user?.name || user?.email}</div>
              <div className="text-[11px] text-gray-400 truncate">{user?.email}</div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">{user?.role}</div>
            </div>
          </div>
        )}
      </div>

      {/* Logout */}
      <div className={'border-t border-gray-100 ' + (collapsed ? 'py-2' : 'p-2')}>
        <button onClick={logout}
          className={'relative w-full flex items-center gap-2.5 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition group '
            + (collapsed ? 'justify-center px-0' : 'px-3')}>
          <i className="ti ti-logout text-base" aria-hidden="true"></i>
          {!collapsed && <span>Sign out</span>}
          {collapsed && <Tooltip label="Sign out" />}
        </button>
      </div>
    </>
  );
}

export default function Layout({ currentPage, onNavigate, children }) {
  const user = getUser();
  const isAgent = user?.role === 'agent';
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('qa_sidebar_collapsed') === '1');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingAppeals, setPendingAppeals] = useState(0);
  const appealsItemId = isAgent ? 'my-appeals' : 'appeals';

  useEffect(() => {
    apiCall(isAgent ? '/my/appeals' : '/appeals')
      .then(d => setPendingAppeals((d.appeals || []).filter(a => a.status === 'pending').length))
      .catch(() => {});
  }, [currentPage, isAgent]);

  const toggleCollapse = () => setCollapsed(c => {
    localStorage.setItem('qa_sidebar_collapsed', c ? '0' : '1');
    return !c;
  });

  const navigate = (id) => { onNavigate(id); setMobileOpen(false); };

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white flex">
      {/* Desktop sidebar */}
      <aside className={'hidden lg:flex flex-col bg-white border-r border-gray-200 fixed h-full z-20 transition-all duration-300 print:hidden '
        + (collapsed ? 'w-14' : 'w-56')}>
        <SidebarContent collapsed={collapsed} currentPage={currentPage} onNavigate={navigate}
          onToggleCollapse={toggleCollapse} pendingAppeals={pendingAppeals} appealsItemId={appealsItemId} />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden print:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-40 px-4 py-3 flex items-center gap-3">
        <button onClick={() => setMobileOpen(o => !o)} className="text-gray-500 hover:text-gray-700" aria-label="Toggle menu">
          <i className={'ti text-xl ' + (mobileOpen ? 'ti-x' : 'ti-menu-2')} aria-hidden="true"></i>
        </button>
        <h1 className="text-base font-medium flex items-center gap-2">
          <i className="ti ti-clipboard-check text-purple-600" aria-hidden="true"></i>
          QA scorecard
        </h1>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden print:hidden fixed top-12 left-0 right-0 bottom-0 bg-black/40 z-30" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile drawer */}
      <aside className={'lg:hidden print:hidden fixed top-12 bottom-0 left-0 w-64 bg-white border-r border-gray-200 z-30 flex flex-col transition-transform duration-300 '
        + (mobileOpen ? 'translate-x-0' : '-translate-x-full')}>
        <SidebarContent collapsed={false} currentPage={currentPage} onNavigate={navigate}
          onToggleCollapse={null} pendingAppeals={pendingAppeals} appealsItemId={appealsItemId} />
      </aside>

      {/* Main content */}
      <main className={'flex-1 pt-12 lg:pt-0 print:pt-0 transition-all duration-300 print:!ml-0 '
        + (collapsed ? 'lg:ml-14' : 'lg:ml-56')}>
        <div className="max-w-4xl mx-auto px-4 py-6 print:max-w-none print:mx-0 print:px-0 print:py-0">
          {children}
        </div>
      </main>
    </div>
  );
}
